import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { env } from "../config/env";
import { authStorage } from "../lib/storage/authStorage";
import { useAuth } from "../context/AuthContext";
import { timeAgo } from "../utils/dateUtils";
import { recommendationsService } from "../features/recommendations/service";

// ─── Types ─────────────────────────────────────────────────────────────────

type LeadStatus = "open" | "accepted" | "contacted" | "closed";

interface Lead {
    _id: string;
    user_id: string;
    session_id: string;
    lead_type?: string;
    status: LeadStatus;
    category?: string;
    destination?: string[];
    trip_type?: string;
    pax?: number | null;
    duration?: string;
    budget?: string;
    travel_dates?: string;
    enquiry_text?: string;
    user_message?: string;
    matched_seller_ids?: string[];
    extra?: {
        deal_id?: string;
        thread_id?: string;
        dealer_id?: string;
        dealer_name?: string;
        deal_title?: string;
    };
    created_at?: string;
    updated_at?: string;
}

type Tab = "sent" | "received";

// ─── API helpers ───────────────────────────────────────────────────────────

const apiBase = env.apiBaseUrl;
const authHeaders = () => {
    const token = authStorage.getToken();
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

async function fetchLeadsSent(): Promise<Lead[]> {
    const res = await fetch(`${apiBase}/rag/leads/me?limit=100`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`Failed to load sent enquiries (${res.status})`);
    const data = await res.json();
    return (data.leads ?? []) as Lead[];
}

async function fetchLeadsReceived(): Promise<Lead[]> {
    const res = await fetch(`${apiBase}/rag/leads/for-seller?limit=100`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`Failed to load incoming enquiries (${res.status})`);
    const data = await res.json();
    return (data.leads ?? []) as Lead[];
}

async function acceptLead(leadId: string): Promise<{ lead_id: string; thread_id: string; status: string }> {
    const res = await fetch(`${apiBase}/rag/leads/${leadId}/accept`, {
        method: "POST",
        headers: authHeaders(),
    });
    if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Accept failed (${res.status}): ${txt || res.statusText}`);
    }
    return res.json();
}

async function patchLeadStatus(leadId: string, nextStatus: LeadStatus): Promise<void> {
    const res = await fetch(`${apiBase}/rag/leads/${leadId}/status`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ status: nextStatus }),
    });
    if (!res.ok) throw new Error(`Update failed (${res.status})`);
}

// ─── Utils ─────────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<LeadStatus, string> = {
    open: "🕐 Open",
    accepted: "✅ Accepted",
    contacted: "💬 Contacted",
    closed: "🔒 Closed",
};

const STATUS_COLOR: Record<LeadStatus, { bg: string; fg: string }> = {
    open: { bg: "#fef3c7", fg: "#92400e" },
    accepted: { bg: "#d1fae5", fg: "#065f46" },
    contacted: { bg: "#dbeafe", fg: "#1e40af" },
    closed: { bg: "#f3f4f6", fg: "#4b5563" },
};

function firstDestination(lead: Lead): string {
    const dests = lead.destination ?? [];
    if (!dests.length) return "—";
    return dests.join(", ");
}

// ─── Page ──────────────────────────────────────────────────────────────────

export default function EnquiriesPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const notifiedLeadId = searchParams.get("lead_id") || "";
    const { user } = useAuth();
    const role = (user?.role ?? "").toLowerCase();
    const isSeller = role === "seller" || role === "dealer" || role === "both" || role === "super_admin";

    const [tab, setTab] = useState<Tab>(isSeller ? "received" : "sent");
    const [sentLeads, setSentLeads] = useState<Lead[]>([]);
    const [receivedLeads, setReceivedLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [actionBusyId, setActionBusyId] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [recLeads, setRecLeads] = useState<any[]>([]);
    const [recLeadsLoading, setRecLeadsLoading] = useState(false);
    const [highlightedRecLeadId, setHighlightedRecLeadId] = useState<string | null>(null);

    const loadTab = useCallback(async (which: Tab) => {
        setLoading(true);
        setError("");
        try {
            if (which === "sent") {
                setSentLeads(await fetchLeadsSent());
            } else {
                setReceivedLeads(await fetchLeadsReceived());
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load enquiries.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadTab(tab);
    }, [tab, loadTab]);

    const currentLeads = tab === "sent" ? sentLeads : receivedLeads;

    const handleAccept = async (lead: Lead) => {
        setActionBusyId(lead._id);
        try {
            const result = await acceptLead(lead._id);
            // Refresh the list + navigate to the DM thread.
            await loadTab("received");
            navigate(`/dm/${result.thread_id}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Accept failed.");
        } finally {
            setActionBusyId(null);
        }
    };

    const handleOpenChat = (lead: Lead) => {
        const threadId = lead.extra?.thread_id;
        if (!threadId) return;
        navigate(`/dm/${threadId}`);
    };

    const handleMarkContacted = async (lead: Lead) => {
        setActionBusyId(lead._id);
        try {
            await patchLeadStatus(lead._id, "contacted");
            await loadTab(tab);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Status update failed.");
        } finally {
            setActionBusyId(null);
        }
    };

    const handleClose = async (lead: Lead) => {
        setActionBusyId(lead._id);
        try {
            await patchLeadStatus(lead._id, "closed");
            await loadTab(tab);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Status update failed.");
        } finally {
            setActionBusyId(null);
        }
    };

    const counts = useMemo(() => ({ sent: sentLeads.length, received: receivedLeads.length }), [sentLeads, receivedLeads]);

    // Status breakdowns
    const receivedStats = useMemo(() => {
        const base = { open: 0, accepted: 0, contacted: 0, closed: 0 };
        for (const l of receivedLeads) base[(l.status ?? "open") as LeadStatus] = (base[(l.status ?? "open") as LeadStatus] ?? 0) + 1;
        return base;
    }, [receivedLeads]);

    const sentStats = useMemo(() => {
        const base = { open: 0, accepted: 0, contacted: 0, closed: 0 };
        for (const l of sentLeads) base[(l.status ?? "open") as LeadStatus] = (base[(l.status ?? "open") as LeadStatus] ?? 0) + 1;
        return base;
    }, [sentLeads]);

    // Load both tabs on mount so counts are always ready
    useEffect(() => {
        void fetchLeadsSent().then(setSentLeads).catch(() => undefined);
        void fetchLeadsReceived().then(setReceivedLeads).catch(() => undefined);
    }, []);

    // Deep link from a lead notification (?lead_id=...) — jump to whichever tab
    // actually has it (buyer's own sent lead, or a lead routed to a seller) and
    // auto-expand it so the notification opens the exact lead, not just the list.
    // A rec-engine routed lead (Formula B) shows up in the "REC ENGINE — Routed
    // Leads" panel above the regular list — when the notified lead is one of
    // those, scroll/highlight there instead, since that's the actual source
    // the notification maps to.
    useEffect(() => {
        if (!notifiedLeadId) return;
        const inRecPanel = recLeads.some((item: any) => (item.lead_id || item.lead?._id) === notifiedLeadId);
        const inReceived = receivedLeads.some((l) => l._id === notifiedLeadId);
        const inSent = sentLeads.some((l) => l._id === notifiedLeadId);
        if (inRecPanel || inReceived) {
            setTab("received");
        } else if (inSent) {
            setTab("sent");
        } else {
            return; // not loaded into any list yet
        }
        if (inRecPanel) {
            setHighlightedRecLeadId(notifiedLeadId);
            requestAnimationFrame(() => {
                document.getElementById(`rec-lead-${notifiedLeadId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
            });
            return;
        }
        setExpanded((x) => ({ ...x, [notifiedLeadId]: true }));
        requestAnimationFrame(() => {
            document.getElementById(`lead-${notifiedLeadId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
    }, [notifiedLeadId, sentLeads, receivedLeads, recLeads]);

    // Rec engine routed leads — sellers only (temp debug)
    useEffect(() => {
        if (!isSeller) return;
        setRecLeadsLoading(true);
        recommendationsService
            .getSellerLeadInbox()
            .then((data) => setRecLeads(data.leads || []))
            .catch(() => {})
            .finally(() => setRecLeadsLoading(false));
    }, [isSeller]);

    return (
        <div className="page-shell">
            <div className="page-container-narrow animate-fade-in">
                <div className="page-header flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <h1 className="page-title">Enquiries</h1>
                        <p className="page-subtitle">
                            Enquiries you&apos;ve sent and enquiries waiting for your response.
                        </p>
                    </div>
                    <button type="button" onClick={() => navigate("/chat")} className="btn-primary shrink-0">
                        + New enquiry
                    </button>
                </div>

                {/* ── Stats bar ───────────────────────────────────────────── */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                    <StatCard label="Sent" value={counts.sent} color="bg-blue-50 text-blue-700 border-blue-200" />
                    <StatCard label="Sent Accepted" value={sentStats.accepted} color="bg-teal-50 text-teal-700 border-teal-200" />
                    <StatCard label="Received" value={counts.received} color="bg-indigo-50 text-indigo-700 border-indigo-200" />
                    <StatCard label="Received Accepted" value={receivedStats.accepted} color="bg-green-50 text-green-700 border-green-200" />
                    <StatCard label="Contacted" value={receivedStats.contacted + sentStats.contacted} color="bg-purple-50 text-purple-700 border-purple-200" />
                    <StatCard label="Closed" value={receivedStats.closed + sentStats.closed} color="bg-gray-50 text-gray-600 border-gray-200" />
                </div>

                {/* ── Per-status breakdown ─────────────────────────────────── */}
                {(tab === "received" ? counts.received : counts.sent) > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {(Object.entries(tab === "received" ? receivedStats : sentStats) as [LeadStatus, number][])
                            .filter(([, n]) => n > 0)
                            .map(([status, n]) => (
                                <span
                                    key={status}
                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border"
                                    style={{ background: STATUS_COLOR[status].bg, color: STATUS_COLOR[status].fg, borderColor: STATUS_COLOR[status].fg + "33" }}
                                >
                                    {STATUS_LABEL[status]} <span className="font-bold">{n}</span>
                                </span>
                            ))}
                    </div>
                )}

                <div className="tabs mb-4">
                    <TabButton
                        active={tab === "sent"}
                        onClick={() => setTab("sent")}
                        label="Sent"
                        count={counts.sent}
                    />
                    <TabButton
                        active={tab === "received"}
                        onClick={() => setTab("received")}
                        label="Received"
                        count={counts.received}
                    />
                </div>

                {error ? <div className="alert-error mb-4">{error}</div> : null}

                {loading ? (
                    <p className="py-8 text-sm text-ink-muted">Loading…</p>
                ) : null}

                {!loading && currentLeads.length === 0 && !error ? (
                    <div className="empty-state">
                        {tab === "sent"
                            ? "You haven't sent any enquiries yet. Browse deals and click 📝 Send Enquiry on any card."
                            : "No incoming enquiries yet. They'll appear here when a buyer submits an enquiry for your destinations or deals."}
                    </div>
                ) : null}

                {/* ── ⚡ REC ENGINE routed leads — temp debug ─────────────── */}
                {isSeller && tab === "received" && (
                    <div className="mb-5 rounded-xl border border-purple-200 bg-purple-50 p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="font-semibold text-purple-700 text-sm">⚡ REC ENGINE — Routed Leads</span>
                            <span className="text-xs text-purple-400">(temp debug — remove after testing)</span>
                        </div>
                        {recLeadsLoading && <p className="text-xs text-purple-400">Loading…</p>}
                        {!recLeadsLoading && recLeads.length === 0 && (
                            <p className="text-xs text-purple-400">No routed leads yet. Appear after buyers send enquiries.</p>
                        )}
                        <div className="flex flex-col gap-2">
                            {recLeads.map((item: any, i: number) => {
                                const lead = item.lead || {};
                                const leadId = item.lead_id || lead._id || "";
                                const dest = Array.isArray(lead.destination)
                                    ? lead.destination.join(", ")
                                    : lead.destination || "—";
                                const score = item.formula_b_score ?? item.score ?? 0;
                                const meta = [
                                    lead.pax ? `${lead.pax} pax` : null,
                                    lead.trip_type || lead.category,
                                    lead.budget,
                                ].filter(Boolean).join(" · ");
                                const busy = actionBusyId === leadId;
                                const isHighlighted = leadId && leadId === highlightedRecLeadId;
                                return (
                                    <div
                                        key={leadId || i}
                                        id={leadId ? `rec-lead-${leadId}` : undefined}
                                        className="rounded-lg bg-white border px-4 py-3 flex items-center justify-between gap-3"
                                        style={isHighlighted ? { borderColor: "#a855f7", borderWidth: 2, boxShadow: "0 0 0 3px rgba(168,85,247,0.15)" } : { borderColor: "#f3e8ff" }}
                                    >
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className="font-medium text-sm text-gray-800">{dest}</div>
                                            {item.buyer_name && (
                                                <div className="text-xs text-gray-400">from {item.buyer_name}</div>
                                            )}
                                            {meta && <div className="text-xs text-purple-500 mt-0.5">{meta}</div>}
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <span className="rounded-full bg-purple-100 text-purple-700 text-xs font-semibold px-3 py-1">
                                                Score {Math.round(score)}
                                            </span>
                                            {lead.user_id && (
                                                <button
                                                    className="btn-primary btn-sm"
                                                    disabled={busy}
                                                    onClick={async () => {
                                                        setActionBusyId(leadId);
                                                        try {
                                                            const res = await fetch(`${apiBase}/api/dm/threads/with-buyer`, {
                                                                method: "POST",
                                                                headers: authHeaders(),
                                                                body: JSON.stringify({ seller_id: lead.user_id }),
                                                            });
                                                            if (!res.ok) {
                                                                const txt = await res.text().catch(() => "");
                                                                throw new Error(txt || res.statusText);
                                                            }
                                                            const data = await res.json();
                                                            navigate(`/dm/${data.thread_id}`);
                                                        } catch {
                                                            setError("Could not start chat for this lead.");
                                                        } finally {
                                                            setActionBusyId(null);
                                                        }
                                                    }}
                                                >
                                                    {busy ? "Opening…" : "💬 Chat with Buyer"}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-3">
                    {currentLeads.map((lead) => {
                        const status = (lead.status ?? "open") as LeadStatus;
                        const statusColors = STATUS_COLOR[status] ?? STATUS_COLOR.open;
                        const isExpanded = expanded[lead._id];
                        const enquiry = lead.enquiry_text || lead.user_message || "";
                        const preview = enquiry.split("\n").slice(0, 2).join(" ").slice(0, 160);
                        const busy = actionBusyId === lead._id;

                        return (
                            <div key={lead._id} id={`lead-${lead._id}`} className="card card-hover card-body">
                                {/* Row 1: title + status */}
                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: 16, color: "#111" }}>
                                            📍 {firstDestination(lead)}
                                        </div>
                                        <div style={{ color: "#6b7280", fontSize: 13, marginTop: 2 }}>
                                            {[
                                                lead.duration,
                                                lead.pax ? `${lead.pax} pax` : null,
                                                lead.budget,
                                                lead.trip_type,
                                                lead.travel_dates,
                                            ].filter(Boolean).join(" · ") || "—"}
                                        </div>
                                    </div>
                                    <span
                                        className="badge shrink-0"
                                        style={{ background: statusColors.bg, color: statusColors.fg }}
                                    >
                                        {STATUS_LABEL[status]}
                                    </span>
                                </div>

                                {/* Row 2: preview */}
                                {enquiry && (
                                    <div style={{ color: "#374151", fontSize: 14, marginTop: 10, lineHeight: 1.5 }}>
                                        {isExpanded ? (
                                            <pre style={{
                                                whiteSpace: "pre-wrap",
                                                fontFamily: "inherit",
                                                margin: 0,
                                                background: "#fafafa",
                                                padding: "10px 12px",
                                                borderRadius: 8,
                                                border: "1px solid #e5e7eb",
                                            }}>{enquiry}</pre>
                                        ) : (
                                            <span>{preview}{enquiry.length > preview.length ? "…" : ""}</span>
                                        )}
                                        {enquiry.length > preview.length && (
                                            <button
                                                onClick={() => setExpanded((x) => ({ ...x, [lead._id]: !isExpanded }))}
                                                className="ml-2 text-sm font-medium text-brand hover:text-brand-hover"
                                            >
                                                {isExpanded ? "Show less" : "Show full enquiry"}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Row 3: footer with meta + actions */}
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, gap: 8, flexWrap: "wrap" }}>
                                    <div style={{ color: "#9ca3af", fontSize: 12 }}>
                                        {timeAgo(lead.created_at ?? "")}
                                        {lead.extra?.deal_title
                                            ? ` · on "${lead.extra.deal_title}"`
                                            : lead.extra?.deal_id
                                                ? ` · deal #${lead.extra.deal_id.slice(-6)}`
                                                : ""}
                                        {tab === "sent" && lead.extra?.dealer_name
                                            ? ` · to ${lead.extra.dealer_name}`
                                            : ""}
                                    </div>
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                        {/* Received tab — seller actions */}
                                        {tab === "received" && status === "open" && (
                                            <button
                                                onClick={() => void handleAccept(lead)}
                                                disabled={busy}
                                                className="btn-primary btn-sm"
                                            >
                                                {busy ? "Accepting…" : "✅ Accept & Start Chat"}
                                            </button>
                                        )}
                                        {tab === "received" && status === "accepted" && lead.extra?.thread_id && (
                                            <>
                                                <button
                                                    onClick={() => handleOpenChat(lead)}
                                                    className="btn-primary btn-sm"
                                                >
                                                    💬 Open Chat
                                                </button>
                                                <button
                                                    onClick={() => void handleMarkContacted(lead)}
                                                    disabled={busy}
                                                    className="btn-secondary btn-sm"
                                                >
                                                    Mark contacted
                                                </button>
                                            </>
                                        )}
                                        {tab === "received" && status === "contacted" && lead.extra?.thread_id && (
                                            <>
                                                <button type="button" onClick={() => handleOpenChat(lead)} className="btn-primary btn-sm">
                                                    💬 Open Chat
                                                </button>
                                                <button type="button" onClick={() => void handleClose(lead)} disabled={busy} className="btn-secondary btn-sm">
                                                    Close
                                                </button>
                                            </>
                                        )}

                                        {/* Sent tab — buyer actions */}
                                        {tab === "sent" && lead.extra?.thread_id && (status === "accepted" || status === "contacted") && (
                                            <button type="button" onClick={() => handleOpenChat(lead)} className="btn-primary btn-sm">
                                                💬 Open Chat
                                            </button>
                                        )}
                                        {tab === "sent" && status === "open" && (
                                            <span style={{ color: "#6b7280", fontSize: 12 }}>
                                                Waiting for seller to accept…
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ─── UI bits ───────────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
    <div className={`rounded-xl border px-4 py-3 flex flex-col gap-0.5 ${color}`}>
        <span className="text-2xl font-bold leading-none">{value}</span>
        <span className="text-xs font-medium opacity-80">{label}</span>
    </div>
);

const TabButton: React.FC<{ active: boolean; onClick: () => void; label: string; count: number }> = ({
    active,
    onClick,
    label,
    count,
}) => (
    <button type="button" onClick={onClick} className={active ? "tab-active" : "tab"}>
        {label}
        {count > 0 ? (
            <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${active ? "bg-brand text-white" : "bg-surface-subtle text-ink-secondary"}`}>
                {count}
            </span>
        ) : null}
    </button>
);
